import { forwardRef } from 'react';
import { Typography, TypographyProps } from '../Typography';

/**
 * H1 heading component. Maps to <h1> element by default for semantic HTML.
 *
 * This is a convenience wrapper around Typography with variant="h1".
 * Use for page titles and top-level headings.
 *
 * @example
 * ```tsx
 * <H1>Page Title</H1>
 * <H1 color="primary" gutterBottom>Colored Title</H1>
 * <H1 component="div">Styled as h1, but rendered as div</H1>
 * ```
 */
export type H1Props = Omit<TypographyProps, 'variant'>;

export const H1 = forwardRef<HTMLElement, H1Props>(({ component = 'h1', ...props }, ref) => (
  <Typography ref={ref} variant="h1" component={component} {...props} />
));

H1.displayName = 'H1';

/**
 * H2 heading component. Maps to <h2> element by default for semantic HTML.
 *
 * This is a convenience wrapper around Typography with variant="h2".
 * Use for section titles.
 *
 * @example
 * ```tsx
 * <H2>Section Title</H2>
 * <H2 color="text.secondary">Muted Section Title</H2>
 * ```
 */
export type H2Props = Omit<TypographyProps, 'variant'>;

export const H2 = forwardRef<HTMLElement, H2Props>(({ component = 'h2', ...props }, ref) => (
  <Typography ref={ref} variant="h2" component={component} {...props} />
));

H2.displayName = 'H2';

/**
 * H3 heading component. Maps to <h3> element by default for semantic HTML.
 *
 * This is a convenience wrapper around Typography with variant="h3".
 * Use for subsection titles.
 *
 * @example
 * ```tsx
 * <H3>Subsection Title</H3>
 * <H3 gutterBottom>Subsection with spacing</H3>
 * ```
 */
export type H3Props = Omit<TypographyProps, 'variant'>;

export const H3 = forwardRef<HTMLElement, H3Props>(({ component = 'h3', ...props }, ref) => (
  <Typography ref={ref} variant="h3" component={component} {...props} />
));

H3.displayName = 'H3';

/**
 * H4 heading component. Maps to <h4> element by default for semantic HTML.
 *
 * This is a convenience wrapper around Typography with variant="h4".
 * Use for small headings.
 *
 * @example
 * ```tsx
 * <H4>Small Heading</H4>
 * <H4 color="primary">Colored Small Heading</H4>
 * ```
 */
export type H4Props = Omit<TypographyProps, 'variant'>;

export const H4 = forwardRef<HTMLElement, H4Props>(({ component = 'h4', ...props }, ref) => (
  <Typography ref={ref} variant="h4" component={component} {...props} />
));

H4.displayName = 'H4';

/**
 * H5 heading component. Maps to <h5> element by default for semantic HTML.
 *
 * This is a convenience wrapper around Typography with variant="h5".
 * Use for emphasized text or small subheadings.
 *
 * @example
 * ```tsx
 * <H5>Emphasized Text</H5>
 * <H5 align="center">Centered Heading</H5>
 * ```
 */
export type H5Props = Omit<TypographyProps, 'variant'>;

export const H5 = forwardRef<HTMLElement, H5Props>(({ component = 'h5', ...props }, ref) => (
  <Typography ref={ref} variant="h5" component={component} {...props} />
));

H5.displayName = 'H5';

/**
 * H6 heading component. Maps to <h6> element by default for semantic HTML.
 *
 * This is a convenience wrapper around Typography with variant="h6".
 * Use for emphasized text or the smallest subheadings.
 *
 * @example
 * ```tsx
 * <H6>Emphasized Text</H6>
 * <H6 color="text.secondary">Muted Subheading</H6>
 * ```
 */
export type H6Props = Omit<TypographyProps, 'variant'>;

export const H6 = forwardRef<HTMLElement, H6Props>(({ component = 'h6', ...props }, ref) => (
  <Typography ref={ref} variant="h6" component={component} {...props} />
));

H6.displayName = 'H6';
